/**
 * 🎯 SYNC STATUS BADGE COMPONENT
 * ============================================================================
 * Mostra lo stato di sincronizzazione dei rapportini duplicati
 * 
 * Stati possibili:
 * - pending: ⏳ In attesa sincronizzazione (giallo)
 * - syncing: 🔄 Sincronizzazione in corso (blu animato)
 * - completed: ✅ Completato (verde, poi nascosto automaticamente)
 * - error: ⚠️ Errore sincronizzazione (rosso)
 * 
 * @version 1.0.0
 * @author Sistema di Gestione Cantieri
 */

import React, { useEffect, useState } from 'react';
import './SyncStatusBadge.css';

const SyncStatusBadge = ({ rapportino }) => {
  const [show, setShow] = useState(true);

  // Configurazione stati
  const statusConfig = {
    pending: {
      color: '#f39c12',
      backgroundColor: '#fff3cd',
      borderColor: '#f39c12',
      icon: '⏳',
      text: 'In attesa sincronizzazione',
      animated: false,
      priority: 1
    },
    syncing: {
      color: '#3498db',
      backgroundColor: '#d6eaf8',
      borderColor: '#3498db',
      icon: '🔄',
      text: 'Sincronizzazione in corso...',
      animated: true,
      priority: 2
    },
    completed: {
      color: '#27ae60',
      backgroundColor: '#d4edda',
      borderColor: '#27ae60',
      icon: '✅',
      text: 'Ore sincronizzate',
      animated: false,
      priority: 3,
      autoHide: true,
      autoHideDelay: 3000
    },
    error: {
      color: '#e74c3c',
      backgroundColor: '#f8d7da',
      borderColor: '#e74c3c',
      icon: '⚠️',
      text: 'Errore sincronizzazione',
      animated: false,
      priority: 4
    }
  };

  // Auto-hide per stato completed
  useEffect(() => {
    if (rapportino?.syncStatus === 'completed') {
      const timer = setTimeout(() => {
        setShow(false);
      }, statusConfig.completed.autoHideDelay);
      
      return () => clearTimeout(timer);
    } else {
      setShow(true);
    }
  }, [rapportino?.syncStatus]);

  // Non mostrare il badge se:
  // 1. Non c'è syncStatus
  // 2. Lo stato è completed e show è false
  // 3. Lo stato è completed e non è recente (più di 5 minuti fa)
  if (!rapportino?.syncStatus) {
    return null;
  }

  if (rapportino.syncStatus === 'completed' && !show) {
    return null;
  }

  // Verifica se lo stato completed è recente
  if (rapportino.syncStatus === 'completed' && rapportino.syncCompletedAt) {
    const completedTime = rapportino.syncCompletedAt?.toDate?.() || new Date(rapportino.syncCompletedAt);
    const now = new Date();
    const minutesAgo = (now - completedTime) / (1000 * 60);
    
    if (minutesAgo > 5) {
      return null; // Non mostrare se è passato più di 5 minuti
    }
  }

  const config = statusConfig[rapportino.syncStatus] || statusConfig.pending;

  return (
    <div 
      className={`sync-status-badge sync-status-${rapportino.syncStatus}`}
      style={{
        backgroundColor: config.backgroundColor,
        borderColor: config.borderColor,
        color: config.color
      }}
    >
      <span 
        className={`sync-icon ${config.animated ? 'sync-icon-animated' : ''}`}
      >
        {config.icon}
      </span>
      <span className="sync-text">{config.text}</span>
      
      {/* Mostra messaggio personalizzato se presente */}
      {rapportino.syncMessage && rapportino.syncMessage !== config.text && (
        <span className="sync-message-detail">
          {rapportino.syncMessage}
        </span>
      )}
    </div>
  );
};

export default SyncStatusBadge;